import { Box } from '@mui/material';
import {
  CarouselControlsArrowsIndex,
  CarouselControlsDownload,
} from './carousel';
import { useEffect, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { image as imageApi } from 'src/backend';

export default function Images({
  images,
  width,
  height,
  objectFit,
  downloadName = 'image',
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const CarouselItem = ({ src }) => {
    return (
      <Box
        component="img"
        sx={{ objectFit: objectFit ?? 'cover', borderRadius: '8px' }}
        src={src}
        width={width}
        height={height}
      />
    );
  };

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const handleSelect = () => setCurrentIndex(emblaApi.selectedScrollSnap());
    handleSelect();
    emblaApi.on('select', handleSelect);
    emblaApi.on('reInit', handleSelect);

    return () => {
      emblaApi.off('select', handleSelect);
      emblaApi.off('reInit', handleSelect);
    };
  }, [emblaApi]);

  const fileExtension = useMemo(() => {
    // The url has the form http://s3-something.amazonaws.com/blah/blah/blah.png
    // We need to extract that ".png" extension.
    const url = images[currentIndex] || '';
    const withoutQuery = url.split('?')[0] || '';
    const filename = withoutQuery.split('/').pop() || '';
    const hasDot = filename.lastIndexOf('.') > -1;
    const ext = filename.substring(filename.lastIndexOf('.') + 1);
    return '.' + (hasDot && ext ? ext : 'png');
  }, [images, currentIndex]);

  /**
   * Downloads the currently displayed image.
   * Attempts to fetch a signed download URL and triggers a download with the appropriate filename.
   * If an error occurs (e.g., network or API failure), falls back to opening the image in the same tab.
   * If both download and fallback fail, does nothing.
   * @async
   * @returns {Promise<void>}
   */
  const handleDownload = async () => {
    try {
      const requestedFilename = `${downloadName}${fileExtension}`;
      const { url } = await imageApi.getDownloadUrl({
        url: images[currentIndex],
        filename: requestedFilename,
        expiresIn: 300,
      });
      const link = document.createElement('a');
      link.href = url;
      link.download = requestedFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      // Fallback: open in same tab if something goes wrong
      try {
        window.location.href = images[currentIndex];
      } catch {
        // no-op
      }
    }
  };

  return (
    <Box width={width} height={height} position="relative">
      <Box
        ref={emblaRef}
        sx={{ overflow: 'hidden', width: '100%', height: '100%' }}
      >
        <Box sx={{ display: 'flex', height: '100%' }}>
          {images.map((image, i) => (
            <Box
              key={i}
              sx={{
                flex: '0 0 100%',
                minWidth: 0,
                height: '100%',
              }}
            >
              <CarouselItem src={image} />
            </Box>
          ))}
        </Box>
      </Box>

      <CarouselControlsArrowsIndex
        index={currentIndex}
        total={images.length}
        onNext={() => emblaApi?.scrollNext()}
        onPrevious={() => emblaApi?.scrollPrev()}
      />
      <CarouselControlsDownload
        onClick={handleDownload}
        index={currentIndex}
        total={images.length}
      />
    </Box>
  );
}
